// Broker Channel Batch (WS6) — broker team registry at /brokers/teams.
// Lightweight management surface (create / edit / archive); team names link
// into the team dashboard. Brokers are Phoenix Capital's external channel
// partners; a team is where Certified brokers graduate to.

import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus } from "lucide-react";
import { brokerTeamsApi } from "@/api/brokerTeams";
import type {
  BrokerTeamSummaryDto,
  UpdateBrokerTeamRequest,
} from "@/api/brokerTeams";
import { extractErrorMessage } from "@/api/client";
import { brokerTeamKeys } from "@/hooks/brokers/brokerKeys";
import { DataTable, type Column } from "@/components/layout/DataTable";
import { PageHeader } from "@/components/layout/PageHeader";
import { Modal } from "@/components/ui/Modal";
import { Textarea } from "@/components/ui/Select";
import { RoleFilteredUserPicker } from "@/components/DealFlow/common/RoleFilteredUserPicker";
import {
  Button,
  Input,
  OverflowMenu,
  StatusBadge,
  useToast,
} from "@/design-system/components/ui";
import { formatDateInTz } from "@/design-system/lib/format";
import { useDateFormatter } from "@/utils/datetime";
import { useAuth } from "@/hooks/useAuth";
import { useDialogs } from "@/hooks/useDialogs";

export function BrokerTeamsPage() {
  const { tz } = useDateFormatter();
  const { hasPermission } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();
  const dialogs = useDialogs();
  const queryClient = useQueryClient();
  const canUpdate = hasPermission("Brokers.Update");

  const [showArchived, setShowArchived] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [editing, setEditing] = useState<BrokerTeamSummaryDto | null>(null);

  const teams = useQuery({
    queryKey: brokerTeamKeys.list(showArchived),
    queryFn: () => brokerTeamsApi.list(showArchived),
  });

  const archiveAction = useMutation({
    mutationFn: ({ team, archive }: { team: BrokerTeamSummaryDto; archive: boolean }) =>
      archive ? brokerTeamsApi.archive(team.id) : brokerTeamsApi.unarchive(team.id),
    onSuccess: (_data, { team, archive }) => {
      void queryClient.invalidateQueries({ queryKey: brokerTeamKeys.all });
      toast.success(`"${team.name}" ${archive ? "archived" : "unarchived"}.`);
    },
    onError: (e) => toast.error(extractErrorMessage(e)),
  });

  async function confirmArchive(team: BrokerTeamSummaryDto, archive: boolean) {
    const ok = await dialogs.confirm({
      title: archive ? "Archive team?" : "Unarchive team?",
      message: archive
        ? `Archive "${team.name}"? Its brokers keep their team assignment.`
        : `Unarchive "${team.name}"?`,
      confirmLabel: archive ? "Archive" : "Unarchive",
      destructive: archive,
    });
    if (!ok) return;
    archiveAction.mutate({ team, archive });
  }

  const columns: Column<BrokerTeamSummaryDto>[] = [
    {
      key: "name",
      header: "Team",
      render: (t) => (
        <Link
          to={`/brokers/teams/${t.id}`}
          className="font-semibold text-gold-shadow hover:underline"
          onClick={(e) => e.stopPropagation()}
        >
          {t.name}
        </Link>
      ),
    },
    {
      key: "owner",
      header: "Owner",
      render: (t) => t.ownerDisplayName ?? <span className="text-gray-mid">—</span>,
    },
    {
      key: "status",
      header: "Status",
      render: (t) => (
        <StatusBadge tone={t.status === "Open" ? "ok" : "neutral"}>{t.status}</StatusBadge>
      ),
    },
    {
      key: "brokers",
      header: "Brokers",
      className: "text-right tabular-nums",
      render: (t) => t.brokerCount,
    },
    {
      key: "created",
      header: "Created",
      render: (t) => (
        <span className="text-small text-gray-mid tabular-nums">
          {formatDateInTz(t.createdAt, tz)}
        </span>
      ),
    },
    ...(canUpdate
      ? [
          {
            key: "actions",
            header: "",
            render: (t: BrokerTeamSummaryDto) => (
              <span onClick={(e) => e.stopPropagation()}>
                <OverflowMenu
                  label={`Actions for ${t.name}`}
                  items={[
                    { label: "Edit", onSelect: () => setEditing(t) },
                    t.status === "Open"
                      ? {
                          label: "Archive",
                          destructive: true,
                          onSelect: () => void confirmArchive(t, true),
                        }
                      : {
                          label: "Unarchive",
                          onSelect: () => void confirmArchive(t, false),
                        },
                  ]}
                />
              </span>
            ),
          } satisfies Column<BrokerTeamSummaryDto>,
        ]
      : []),
  ];

  return (
    <>
      <PageHeader
        eyebrow="Partners"
        title="Broker teams"
        description="Graduated channel-partner brokers, grouped into production teams."
        actions={
          canUpdate ? (
            <Button size="sm" onClick={() => setCreateOpen(true)} data-testid="teams-add">
              <Plus size={14} strokeWidth={1.5} aria-hidden /> New team
            </Button>
          ) : undefined
        }
      />

      <div className="flex flex-col gap-space-3" data-testid="broker-teams-page">
        <label className="inline-flex items-center gap-space-1 text-small text-onyx cursor-pointer self-start">
          <input
            type="checkbox"
            checked={showArchived}
            onChange={(e) => setShowArchived(e.target.checked)}
            data-testid="teams-show-archived"
          />
          Show archived
        </label>

        <DataTable<BrokerTeamSummaryDto>
          columns={columns}
          rows={teams.data ?? []}
          loading={teams.isLoading}
          error={teams.isError ? extractErrorMessage(teams.error) : null}
          emptyMessage="No broker teams yet. Create one to start graduating Certified brokers."
          rowKey={(t) => t.id}
          onRowClick={(t) => navigate(`/brokers/teams/${t.id}`)}
          data-testid="broker-teams-table"
        />
      </div>

      {createOpen && (
        <TeamEditModal
          onClose={() => setCreateOpen(false)}
          onSaved={(name) => {
            setCreateOpen(false);
            toast.success(`Team "${name}" created.`);
          }}
        />
      )}
      {editing && (
        <TeamEditModal
          team={editing}
          onClose={() => setEditing(null)}
          onSaved={(name) => {
            setEditing(null);
            toast.success(`Team "${name}" updated.`);
          }}
        />
      )}
    </>
  );
}

function TeamEditModal({
  team,
  onClose,
  onSaved,
}: {
  team?: BrokerTeamSummaryDto;
  onClose: () => void;
  onSaved: (name: string) => void;
}) {
  const queryClient = useQueryClient();
  const [name, setName] = useState(team?.name ?? "");
  const [description, setDescription] = useState(team?.description ?? "");
  const [ownerUserId, setOwnerUserId] = useState<string | null>(team?.ownerUserId ?? null);
  const [error, setError] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: () => {
      const trimmed = name.trim();
      if (team) {
        // FIND-12: the form hydrates every field, so always send all of them
        // ("" clears description / owner).
        const body: UpdateBrokerTeamRequest = {
          name: trimmed,
          description,
          ownerUserId: ownerUserId ?? "",
        };
        return brokerTeamsApi.update(team.id, body);
      }
      return brokerTeamsApi.create({
        name: trimmed,
        description: description || null,
        ownerUserId,
      });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: brokerTeamKeys.all });
      onSaved(name.trim());
    },
    onError: (e) => setError(extractErrorMessage(e)),
  });

  function submit() {
    setError(null);
    if (!name.trim()) {
      setError("Name is required.");
      return;
    }
    save.mutate();
  }

  return (
    <Modal
      open
      onClose={onClose}
      title={team ? "Edit team" : "New team"}
      size="sm"
      footer={
        <div className="flex justify-end gap-space-2">
          <Button variant="secondary" size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button
            size="sm"
            loading={save.isPending}
            disabled={!name.trim() || save.isPending}
            onClick={submit}
            data-testid="team-save"
          >
            {team ? "Save" : "Create"}
          </Button>
        </div>
      }
    >
      <div className="grid gap-space-3" data-testid="team-edit-modal">
        <div className="flex flex-col gap-space-1">
          <span className="text-caption text-gray-mid uppercase tracking-label">Name</span>
          <Input
            value={name}
            autoFocus
            onChange={(e) => setName(e.target.value)}
            data-testid="team-name-input"
          />
        </div>
        <div className="flex flex-col gap-space-1">
          <span className="text-caption text-gray-mid uppercase tracking-label">
            Description (optional)
          </span>
          <Textarea value={description} rows={3} onChange={(e) => setDescription(e.target.value)} />
        </div>
        <div className="flex flex-col gap-space-1">
          <span className="text-caption text-gray-mid uppercase tracking-label">
            Owner (optional)
          </span>
          <RoleFilteredUserPicker
            role="C1,C0,Admin"
            value={ownerUserId}
            onChange={setOwnerUserId}
            placeholder="Unassigned"
            aria-label="Team owner"
          />
        </div>
        {error && (
          <p className="text-label text-status-error m-0" role="alert">
            {error}
          </p>
        )}
      </div>
    </Modal>
  );
}
